// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../../reducers/reducers';
import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import { selectAppPlatform, selectAppTimeFormatSetting, selectEffectiveTimezone } from '../../selectors/selectors';
import { CheckCircleFilled, RetweetOutlined } from '@ant-design/icons';
import { allLifecycleEventsForPlatform } from '../automations/automationHelperComponents';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { CompletedReleaseSummary, LifecycleStep, NextReleaseSummary, UpcomingReleaseSummary } from '../../types';
import Divider from 'antd/lib/divider';
import { FeatureSection } from '../../typescriptHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { useReleaseLifecycleStepDisplayInfo } from '@/utils/hooks/helpers';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';

interface TargetDatesSectionProps {
    appId: string;
    releaseId: string;
}

const TargetDatesSection: React.FC<TargetDatesSectionProps> = (props: TargetDatesSectionProps) => {
    const { appId, releaseId } = props;

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const lifecycleSteps = allLifecycleEventsForPlatform(appPlatform);

    return (
        <EmptyStateWrapper
            appId={appId}
            featureSections={[FeatureSection.OverviewTopOverview]}
            releaseId={releaseId}
            customWrapperStyle={{
                minHeight: 'unset',
                padding: '12px 24px',
                margin: '24px 0',
                marginRight: 'auto',
            }}
        >
            {(isInEmptyState) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        padding: isInEmptyState ? 20 : '10px 0px 24px 0px',
                    }}
                >
                    {lifecycleSteps.map((step, index) => {
                        return (
                            <div key={index} style={{ display: 'flex', minHeight: '100px' }}>
                                {index ?
                                    <Divider type="vertical" style={{ height: '100%', margin: '0px 20px' }} />
                                :   ''}
                                <DateBlock step={step} releaseId={releaseId} appId={appId} />
                            </div>
                        );
                    })}
                </div>
            )}
        </EmptyStateWrapper>
    );
};

export const DateBlock = ({
    step,
    releaseId,
    appId,
    releaseSummary,
}: {
    step: LifecycleStep;
    releaseId: string;
    appId: string;
    releaseSummary?: NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary;
}) => {
    const {
        automationIcon,
        showCadenceIcon,
        isComplete,
        automationEnabled,
        targetDate,
        displayText,
        hasDate,
        displayTime,
        displayDate,
        displayTimeMoment,
    } = useReleaseLifecycleStepDisplayInfo(
        step,
        releaseId,
        appId,
        false,
        releaseSummary,
        'shortHourOnlyWithoutTimezone',
    );

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    return (
        <div style={{ minWidth: '180px' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    gap: '10px',
                }}
            >
                {automationIcon}
                {showCadenceIcon && (
                    <RetweetOutlined
                        style={{
                            fontSize: 14,
                            color: antGrey.grey6,
                        }}
                    />
                )}
                <Text style={{ fontSize: '14px', color: antGrey.grey6 }}>
                    {!isComplete && (automationEnabled || targetDate.isValid()) ?
                        'Target ' + displayText + ' Date'
                    :   displayText}
                </Text>
                {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
            </div>
            <div>
                {displayTimeMoment && effectiveTimezone ?
                    <TimeWithConditionalTz
                        triggeredAtLabel={displayTime}
                        triggeredByMoment={displayTimeMoment}
                        effectiveTimezone={effectiveTimezone}
                        use24HourTimeFormat={use24HourTimeFormat}
                        style={{ fontSize: 18, color: antGrey.grey8 }}
                    />
                :   <Text
                        style={{
                            fontSize: 18,
                            color: !hasDate ? antGrey.grey6 : antGrey.grey8,
                        }}
                    >
                        {displayTime}
                    </Text>
                }
            </div>
            <div>
                <Text
                    style={{
                        fontSize: '18px',
                        color: `${!hasDate ? antGrey.grey6 : antGrey.grey8}`,
                        fontWeight: 600,
                    }}
                >
                    {displayDate}
                </Text>
            </div>
        </div>
    );
};

export default TargetDatesSection;
